import React from 'react';
import { Document, Page } from 'react-pdf';

import {
  Button,
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  Skeleton,
  Stack,
  Text,
} from '@eluve/components';

import { usePdfContext } from './PdfContext';

const LoadingSkeleton = () => (
  <Stack align="center" justify="center" gap={4} className="p-8">
    <Skeleton className="h-[600px] w-[450px]" />
    <Skeleton className="h-4 w-32" />
  </Stack>
);

const ErrorMessage: React.FC<{ message: string }> = ({ message }) => (
  <Stack
    align="center"
    justify="center"
    className="p-8"
    gap={2}
    direction="horizontal"
  >
    <Text inline color="primary">
      {message}
    </Text>
  </Stack>
);

export const PdfPreviewDialog: React.FC = () => {
  const {
    showPreview,
    setShowPreview,
    currentPreviewFile,
    currentPage,
    totalPages,
    loading,
    error,
    onDocumentLoadSuccess,
    textRenderer,
    handlePrevPage,
    handleNextPage,
  } = usePdfContext();
  return (
    <Dialog open={showPreview} onOpenChange={setShowPreview}>
      <DialogContent size="4xl" className="h-[80vh]">
        <DialogHeader>
          <DialogTitle>{currentPreviewFile?.fileName}</DialogTitle>
        </DialogHeader>

        {/*  Navigation */}
        {totalPages > 1 && (
          <Stack
            gap={3}
            justify="center"
            className="border-b pb-4"
            direction="horizontal"
          >
            <Button
              variant="outline"
              size="sm"
              onClick={handlePrevPage}
              disabled={currentPage <= 1}
            >
              ← Prev
            </Button>
            <Text inline size="xs" color="secondary">
              Page {currentPage} of {totalPages}
            </Text>
            <Button
              variant="outline"
              size="sm"
              onClick={handleNextPage}
              disabled={currentPage >= totalPages}
            >
              Next →
            </Button>
          </Stack>
        )}

        <div className="flex-1 overflow-auto">
          <Stack direction="horizontal" justify="center" align="stretch">
            {loading && <LoadingSkeleton />}

            {error && <ErrorMessage message={error} />}

            {!loading && !error && currentPreviewFile?.fileUrl && (
              <Document
                file={currentPreviewFile.fileUrl}
                onLoadSuccess={onDocumentLoadSuccess}
              >
                <Page
                  pageNumber={currentPage}
                  scale={1.2}
                  customTextRenderer={textRenderer}
                  loading={<LoadingSkeleton />}
                  error={
                    <ErrorMessage
                      message={`Failed to load page ${currentPage}`}
                    />
                  }
                />
              </Document>
            )}
          </Stack>
        </div>
      </DialogContent>
    </Dialog>
  );
};
